import React, { Dispatch, memo, SetStateAction, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { GridArrowDownwardIcon, GridExpandMoreIcon } from '@mui/x-data-grid-pro';
import { getCultureCampaignKey } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { AutocompleteOption, IParcelImported } from './ProcedureImport';
import { ICampaign } from 'components/generics/Interface/Exploitation/Parcels/ICampaign';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import { useTranslation } from 'react-i18next';

interface OptimizedCardProps {
  parcel: IParcelImported;
  baseAutocompleteOptions: AutocompleteOption[];
  isExpanded: boolean;
  forecastCampaign: ICampaign;
  handleAccordionChange: (
    uuid: string
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  handleChange: (
    importedParcelId: string
  ) => (
    event: React.SyntheticEvent<Element, Event>,
    value: any,
    reason: any,
    details?: any
  ) => void;
  handleChangeParcelName: (event: SelectChangeEvent, uuid: string) => void;
  setParcelAssoliaHover: (parcelId: number | string) => void;
  setParcelImportHover: (parcelId: number | string) => void;
  chosenNames: Map<string, string>;
  highlightedCard: string | null;
  setParcelFromCardToZoom: Dispatch<
    SetStateAction<{
      assoliaParcel: string | number;
      importedParcel: string | number;
      coordinates: any[];
      timestamp: number;
    }>
  >;
}

const InfoParcel = memo(({ value }: { value: any }) => {
  const { t } = useTranslation();
  if (!value) {
    value = t('common.unknown');
  }

  return (
    <Box className="accordion_details_compare_content">
      <Typography>{value}</Typography>
    </Box>
  );
});

InfoParcel.displayName = 'InfoParcel';

const OptimizedCard = memo<OptimizedCardProps>(
  ({
    parcel,
    baseAutocompleteOptions,
    isExpanded,
    forecastCampaign,
    handleAccordionChange,
    handleChange,
    handleChangeParcelName,
    setParcelAssoliaHover,
    setParcelImportHover,
    chosenNames,
    highlightedCard,
    setParcelFromCardToZoom,
  }) => {
    const { t } = useTranslation();
    return (
      <Card
        onMouseEnter={() => {
          setParcelAssoliaHover(parcel?.matchedParcel?.id ?? 'none');
          setParcelImportHover(parcel?.importedParcel?.uuid ?? 'none');
        }}
        onMouseLeave={() => {
          setParcelAssoliaHover('none');
          setParcelImportHover('none');
        }}
        id={parcel.importedParcel.uuid}
        className={`imported_parcel_card ${
          highlightedCard === parcel.importedParcel.uuid ? 'card-highlighted' : ''
        }`}
      >
        <CardHeader
          className="imported_parcel_card_header"
          classes={{ content: 'imported_parcel_card_header_content' }}
          title={
            <Accordion
              expanded={isExpanded}
              onChange={handleAccordionChange(parcel.importedParcel.uuid)}
            >
              <AccordionSummary
                expandIcon={<GridExpandMoreIcon />}
                id={'panel-header-' + parcel.importedParcel.uuid}
                className="accordion_summary"
                classes={{ content: 'accordion_summary_content_wrapper' }}
              >
                <Box className="accordion_summary_content">
                  <span className="accordion_summary_parcel_name">
                    {parcel.importedParcel.name}
                  </span>
                  <span>{parcel.importedParcel.surface} hec.</span>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="accordion_details">
                <Box className="accordion_details_container">
                  <div className="accordion_details_parcel_name">
                    {parcel.importedParcel?.coordinates?.length ? (
                      <div className="icon_label">
                        <Button
                          onClick={() =>
                            setParcelFromCardToZoom({
                              assoliaParcel: parcel?.matchedParcel?.id || 'none',
                              importedParcel: parcel.importedParcel.uuid,
                              coordinates: parcel.importedParcel.coordinates || [],
                              timestamp: Date.now(),
                            })
                          }
                          title="Zoomer la carte sur cette parcelle"
                          variant="text"
                          component="label"
                          startIcon={<CenterFocusWeakIcon classes={{ root: 'icon_label_icon' }} />}
                          className="icon_label_button"
                        >
                          {t('common.name')} :
                        </Button>
                      </div>
                    ) : (
                      <span className="classic_name_label">{t('common.name')} :</span>
                    )}
                    <div className="accordion_details_parcel_name_text">
                      {parcel.importedParcel.name}
                    </div>
                  </div>
                  {parcel.matchedParcel?.name && (
                    <FormControl fullWidth>
                      <InputLabel id={'import-parcel-name' + parcel.importedParcel.uuid}>
                        {t('label.keptNameAfterImport')}
                      </InputLabel>
                      <Select
                        labelId={'import-parcel-name' + parcel.importedParcel.uuid}
                        id={'import-parcel-name-select' + parcel.importedParcel.uuid}
                        value={chosenNames.get(parcel.importedParcel.uuid)}
                        label={t('label.keptNameAfterImport')}
                        onChange={(event) =>
                          handleChangeParcelName(event, parcel.importedParcel.uuid)
                        }
                      >
                        <MenuItem value={parcel.matchedParcel?.name}>
                          {parcel.matchedParcel.name}
                        </MenuItem>
                        <MenuItem value={parcel.importedParcel.name}>
                          {parcel.importedParcel.name}
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <div className="accordion_details_text">
                    {parcel.matchedParcel !== null && (
                      <div className="accordion_details_divider"></div>
                    )}
                    {parcel.matchedParcel !== null && (
                      <div className="accordion_details_header">
                        <div className="accordion_details_header_title">{t('label.imported')}</div>
                        <div className="accordion_details_header_title">{t('label.assolia')}</div>
                      </div>
                    )}
                    <div className="accordion_details_card">
                      <span>{t('common.surface')}</span>
                      <div className="accordion_details_compare">
                        <InfoParcel value={parcel.importedParcel.surface} />
                        {parcel.matchedParcel !== null && (
                          <InfoParcel value={parcel.matchedParcel?.surface ?? null} />
                        )}
                      </div>
                    </div>
                    {forecastCampaign && (
                      <div className="accordion_details_card">
                        <span>{getCultureCampaignKey(1, forecastCampaign)}</span>
                        <div className="accordion_details_compare">
                          <InfoParcel
                            value={
                              parcel.importedParcel.parcelCultures?.[
                                getCultureCampaignKey(1, forecastCampaign)
                              ]?.name ?? t('message.noCultureAssociated')
                            }
                          />
                          {parcel.matchedParcel !== null && (
                            <InfoParcel
                              value={
                                parcel.matchedParcel?.parcelCultures?.[
                                  getCultureCampaignKey(1, forecastCampaign)
                                ]?.name ?? t('message.noCultureAssociated')
                              }
                            />
                          )}
                        </div>
                      </div>
                    )}
                    <div className="accordion_details_card">
                      <span>{t('label.groundType')}</span>
                      <div className="accordion_details_compare">
                        <InfoParcel value={parcel.importedParcel.groundType.name ?? null} />
                        {parcel.matchedParcel !== null && (
                          <InfoParcel value={parcel.matchedParcel?.groundType?.name ?? null} />
                        )}
                      </div>
                    </div>
                    <div className="accordion_details_card">
                      <span>{t('common.irrigability')}</span>
                      <div className="accordion_details_compare">
                        <InfoParcel
                          value={parcel.importedParcel.isIrrigable ? t('common.yes') : t('common.no')}
                        />
                        {parcel.matchedParcel !== null && (
                          <InfoParcel
                            value={
                              parcel.matchedParcel?.isIrrigable
                                ? t('common.yes')
                                : parcel.matchedParcel?.isIrrigable === false
                                ? t('common.no')
                                : t('common.unknown')
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Box>
              </AccordionDetails>
            </Accordion>
          }
        />
        <CardActions className="card_action">
          {baseAutocompleteOptions && (
            <Autocomplete
              autoComplete
              clearText={t('button.removeCorrespondence')}
              forcePopupIcon={false}
              options={baseAutocompleteOptions}
              noOptionsText={t('message.noParcelAvailable')}
              renderGroup={(params) => (
                <Box key={params.key}>
                  <Box className="imported_parcels_grouping_list_title">
                    <Typography
                      className="imported_parcels_grouping_list_option"
                      variant="subtitle1"
                    >
                      <GridArrowDownwardIcon /> {params.group}
                    </Typography>
                  </Box>
                  {params.children}
                </Box>
              )}
              value={
                parcel.matchedParcel
                  ? {
                      label: parcel.matchedParcel.name,
                      value: parcel.matchedParcel.id,
                    }
                  : null
              }
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              onChange={(event, newValue) => {
                handleChange(parcel.importedParcel.uuid)(
                  event,
                  newValue,
                  newValue ? 'selectOption' : 'clear'
                );
              }}
              sx={{ width: '100%' }}
              renderOption={(props, option, state, ownerState) => {
                const { key, ...optionProps } = props;

                if (parcel.matchedParcel && option.value === parcel.matchedParcel?.id) {
                  return null;
                }

                return (
                  <Box
                    key={key}
                    sx={{
                      opacity: '1 !important',
                      backgroundColor: 'transparent !important',
                      '&:hover': {
                        backgroundColor: '#cbcbcb !important',
                      },
                    }}
                    component="li"
                    {...optionProps}
                  >
                    {ownerState.getOptionLabel(option)}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('label.correspondingAssoliaParcel')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: parcel.matchedParcel === null ? '#ffebee' : 'transparent',
                    },
                  }}
                />
              )}
            />
          )}
        </CardActions>
      </Card>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.parcel.importedParcel.uuid === nextProps.parcel.importedParcel.uuid &&
      prevProps.parcel.matchedParcel?.id === nextProps.parcel.matchedParcel?.id &&
      prevProps.chosenNames.get(prevProps.parcel.importedParcel.uuid) ===
        nextProps.chosenNames.get(nextProps.parcel.importedParcel.uuid) &&
      prevProps.isExpanded === nextProps.isExpanded &&
      prevProps.baseAutocompleteOptions === nextProps.baseAutocompleteOptions &&
      prevProps.highlightedCard === nextProps.highlightedCard
    );
  }
);

OptimizedCard.displayName = 'OptimizedCard';

export default OptimizedCard;
